import type { GalleryData_Item } from './../../ricos-schema/legacy/wix/rich_content/v1/index.js';
import type { AnchorTarget, RelValue } from './../../ricos-types/index.js';
import { getLinkHrefUrl } from './../link/get-link-href-url.js';
import { normalizeHref } from './../link/normalize-href.js';
import { uuid } from './../../wix-rich-content-common/index.js';

export type GalleryItem = {
  mediaUrl: string;
  url: string;
  metaData: {
    type: 'image' | 'video';
    title?: string;
    alt?: string;
    width: number;
    height: number;
    link?: {
      type?: string;
      target: string;
      rel?: string;
      url?: string;
      data?: {
        type: string;
        rel: string;
        url: string;
        target: string;
        anchorName?: string;
      };
    };
    poster?: {
      url: string;
      width: number;
      height: number;
    };
  };
  directLink?: {
    url: string;
    target: string;
    rel: string;
  };
  itemId: string;
  hash: string;
};

export const convertToGalleryItem = ({
  item,
  index,
  relValue,
  anchorTarget,
  baseUrl,
}: {
  item: GalleryData_Item;
  index: number;
  relValue?: RelValue;
  anchorTarget?: AnchorTarget;
  baseUrl?: string;
}) => {
  const { image, video, title, altText } = item;
  const media = image?.media || video?.media;
  const mediaUrl = media?.src?.url || media?.src?.id || '';
  const width = media?.width || 0;
  const height = media?.height || 0;
  const convertedData: GalleryItem = {
    mediaUrl,
    url: mediaUrl,
    metaData: {
      type: image ? 'image' : 'video',
      height,
      width,
      title,
      alt: altText,
    },
    // uuid is used to guarentee there are no 2 gallery items on the DOM with the same ID (on different galleries) due to a pro gallery issues with videos - breaks the video controls behavoiur.
    itemId: uuid() + '_' + index.toString(),
    hash: mediaUrl + '_' + index.toString(),
  };
  if (video) {
    convertedData.metaData.link = {
      target: '_blank',
      rel: 'noopener',
      url: normalizeHref(mediaUrl) ?? '',
    };
    const { src, width: thumbnailWidth, height: thumbnailHeight } = video.thumbnail || {};
    if (src?.url || src?.id) {
      convertedData.metaData.poster = {
        url: src.url || src.id || '',
        width: thumbnailWidth || width,
        height: thumbnailHeight || height,
      };
    }
  }
  if (image?.link) {
    const { url: linkUrl = '', target: linkTarget, rel: linkRel, anchor } = image.link;

    const target = (linkTarget && `_${linkTarget.toLowerCase()}`) || anchorTarget || '_self';
    let rel = relValue || 'noopener';

    if (linkRel) {
      rel = 'noopener';
      linkRel.nofollow && (rel += ' nofollow');
      linkRel.sponsored && (rel += ' sponsored');
      linkRel.ugc && (rel += ' ugc');
      (linkRel.noreferrer || linkRel.noreferrer === undefined) && (rel += ' noreferrer');
    }

    const normalizedUrl = normalizeHref(linkUrl) ?? '';

    const url = normalizedUrl
      ? normalizedUrl
      : getLinkHrefUrl(anchor ? { anchor } : { url: linkUrl }, baseUrl) ?? '';

    convertedData.metaData.link = {
      type: 'wix',
      target,
      data: {
        type: anchor ? 'AnchorLink' : 'ExternalLink',
        target,
        rel,
        url,
        anchorName: anchor,
      },
    };

    convertedData.directLink = {
      url,
      target,
      rel,
    };
  }
  return convertedData;
};
